import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Typography,
    Chip,
    Table, TableHead, TableRow, TableCell, TableBody,
    TablePagination,
    Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import ScreenBanner from "../../../../../components/ScreenBanner";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import SkeletonListarOcs from "./SkeletonListarOcs";
import PermissionGuard from "../../../../../components/PermissionGuard";
import { OrdemCompra } from "../../../../../types/api/estoque/pesquisar-ordens-compra";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import DialogListarOCFilter from "./components/DialogListarOCFilter";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";
import { processUrlFilters } from "../../../../../components/TableDataGridV2/helpers";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import { TFilters } from "../../../../../components/TableDataGridV2/types";
import { ModalDetalheOCProvider, useModalDetalheOC } from "../ModalDetalheOrdemCompraContext";
function ListarOcsContent() {

    const { openModal } = useModalDetalheOC();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL();

    const [filtros, setFiltros] = useState<TFiltroListarOcs>({});
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [openFiltros, setOpenFiltros] = useState<boolean>(false);
    const debouncedKeyword = useDebounce(filtros.palavraChave ?? '', 1000);

    const inputRef = useRef<HTMLInputElement>(null);

    const queryParams = useMemo(() => {
        const params: Record<string, any> = {
            page: page + 1,
            rowsPerPage,
        };

        if (debouncedKeyword) {
            params.pesquisar = debouncedKeyword;
        }

        if (filtros.status && filtros.status.length > 0) {
            params.status = Array.isArray(filtros.status)
                ? filtros.status.join(',')
                : filtros.status;
        }

        if (filtros.somenteValorOperacao !== undefined) {
            params.somente_valor_operacao = filtros.somenteValorOperacao;
        }

        return params;
    }, [debouncedKeyword, filtros, page, rowsPerPage]);

    const { data, isFetching } = useQuery({
        queryKey: ['ordens-compra', queryParams],
        queryFn: async () => {
            const response = await axiosClient.get("/estoque/compras/pesquisar-ordem-compra", {
                params: queryParams
            });
            return response.data;
        },
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    const ordens = data?.data || [];
    const total = data?.total || 0;


    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['palavraChave']
        );
    }, [filtros]);

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };

        if (item.startsWith('Status:')) {
            delete newFiltros.status;
        } else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TFiltroListarOcs];
                }
            });
        }

        setFiltros(newFiltros);
    }

    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = useCallback((pesquisa: string) => {
        const urlFilters = processUrlFilters({}, searchParams);
        urlFilters.pesquisar = pesquisa || undefined;
        setMultipleParamsUrl(urlFilters);
        setFiltros((prevState) => ({ ...prevState, palavraChave: pesquisa }));
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleApplyFilters = (newFilters: TFiltroListarOcs) => {
        const novoObjeto: TFilters = { ...newFilters };
        Object.keys(newFilters).forEach((key) => {
            if (key.startsWith('status')) {
                novoObjeto.status = [novoObjeto.status];
            }
            if (key === 'somenteValorOperacao') {
                novoObjeto.somente_valor_operacao = novoObjeto.somenteValorOperacao;
                delete novoObjeto[key];
            }
            if (key.startsWith('palavraChave')) {
                novoObjeto.pesquisar = novoObjeto.palavraChave;
                delete novoObjeto[key];
            }

        });
        setMultipleParamsUrl(novoObjeto);

        setFiltros(newFilters);
        setPage(0);
    }

    useEffect(() => {
        const urlFilters = processUrlFilters({}, searchParams);
        if (urlFilters && Object.keys(urlFilters).length > 0) {
            const filtrosFromUrl: TFiltroListarOcs = {};
            if (urlFilters.pesquisar) {
                filtrosFromUrl.palavraChave = urlFilters.pesquisar;
            }
            if (urlFilters.status) {
                filtrosFromUrl.status = urlFilters.status;
            }
            if (urlFilters.somente_valor_operacao) {
                filtrosFromUrl.somenteValorOperacao = urlFilters.somente_valor_operacao;
            }

            setFiltros(filtrosFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <Box sx={{ background: "#F5F6FA", minHeight: "100vh" }}>
            <Container maxWidth="lg">
                <Typography textAlign="center" variant="h5" mt={3} mb={2}>
                    Listagem de Ordens de Compra
                </Typography>


                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                    }}
                >
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <SearchWithFilters
                                palavraChave={filtros.palavraChave ?? ''}
                                textFieldProps={{
                                    inputRef: inputRef,
                                    label: "Pesquisar",
                                    placeholder: "Pesquise por número da solicitação",
                                    size: "medium",
                                }}
                                pesquisar={handleSearch}
                                setTermoPesquisa={handleSearch}
                                handleOpenFilters={() => setOpenFiltros(true)}
                            />

                            <ChipList
                                items={chipItems}
                                onDelete={handleDeleteFilter}
                            />
                        </Grid>
                        {
                            isFetching && (
                                <SkeletonListarOcs />
                            )
                        }
                        {
                            !isFetching &&
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ width: "100%", overflowX: "auto" }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>Ordem</b></TableCell>
                                                <TableCell><b>Fornecedor</b></TableCell>
                                                <TableCell><b>Contato</b></TableCell>
                                                <TableCell align="right"><b>Valor</b></TableCell>
                                                <TableCell align="right"><b>Status</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ordens.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5}>
                                                        <DisplayNotFoundInfo
                                                            label="Nenhuma ordem de compra encontrada" />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                ordens.map((row: OrdemCompra) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell>
                                                            <Button
                                                                variant="text"
                                                                sx={{
                                                                    color: "primary",
                                                                    textDecoration: "underline",
                                                                    fontWeight: "bold",
                                                                    fontSize: "1rem",
                                                                    padding: 0,
                                                                    minWidth: "unset"
                                                                }}
                                                                onClick={() => {
                                                                    setParamsUrl(row.codigo.toString(), "detalhe-ordem-compra");
                                                                    openModal(row.codigo.toString());
                                                                }}
                                                            >
                                                                {row.codigo}
                                                            </Button>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: "#888", fontSize: "0.9rem", mt: 0.5 }}
                                                            >
                                                                Emitida em {row.data_emissao ? new Date(row.data_emissao).toLocaleDateString() : "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack gap={1}>
                                                                <Typography variant="body2">
                                                                    {row.fornecedor ? row.fornecedor : "-"}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack gap={1}>
                                                                <Typography variant="body2">
                                                                    {row.cotacao ? row.cotacao.vendedor.nome_vendedor : "-"}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography variant="body1">
                                                                {row.valor ? formatarValorMonetario(Number(row.valor)) : "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip
                                                                label={getStatusChipProps(row.status).label}
                                                                sx={getStatusChipProps(row.status).sx}
                                                                color="default"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                    <Stack direction="row" justifyContent="flex-end">
                                        <TablePagination
                                            component="div"
                                            count={total}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            rowsPerPageOptions={[10, 20, 50, 100]}
                                            labelRowsPerPage="Linhas por página:"
                                            labelDisplayedRows={({ from, to, page }) => `Pág. ${page + 1}`}
                                        />
                                    </Stack>
                                </Box>
                            </Grid>
                        }
                        <DialogListarOCFilter
                            open={openFiltros}
                            onClose={() => setOpenFiltros(false)}
                            onConfirm={handleApplyFilters}
                            filtrosAtuais={filtros}
                        />
                    </Grid>
                </Paper>

            </Container>
        </Box>
    );
}

export default function ListarOcs() {
    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Estoque" />
            <PermissionGuard permissionId="estoque.listar-ordens-compra">
                <ModalDetalheOCProvider>
                    <ListarOcsContent />
                </ModalDetalheOCProvider>
            </PermissionGuard>
        </>
    );
}

function getStatusChipProps(status: string) {
    let background = "#E0E0E0";
    let textColor = "#444";
    if (status === "Finalizado") {
        background = "#2E7D32";
        textColor = "#fff";
    }
    if (status === "Em andamento") {
        background = "#FFD600";
        textColor = "#444";
    }
    if (status === "Cancelada") {
        background = "#D32F2F";
        textColor = "#fff";
    }
    if (status === "Em aberto") {
        background = "#46B36F";
        textColor = "#fff";
    }
    return {
        sx: {
            fontWeight: "bold",
            fontSize: "0.8125rem",
            px: 2,
            height: 28,
            borderRadius: 5,
            background,
            color: textColor,
        },
        color: "default",
        label: status,
    };
}

export interface TFiltroListarOcs {
    status?: string[];
    palavraChave?: string;
    somenteValorOperacao?: boolean;
}

const filtersChipLabels: { [key in keyof Omit<TFiltroListarOcs, 'palavraChave'>]: string } = {
    status: "Status",
    somenteValorOperacao: "Somente valor da operação"
};